import { Controller } from "@hotwired/stimulus"

// "Run a card" — two ways to set the amount, one amount that gets charged.
//
//   Pay a balance: tap a unit (or the whole account) and its POSTED balance
//     fills the amount + attributes the charge to that unit. Under dual pricing
//     the posted balance already includes the card adjustment, so the tapped
//     number is charged as-is — there is no "+3%" step, here or anywhere.
//   Other charge: type any amount (a partial, a prepayment) against a chosen
//     unit. Charged exactly as entered; nothing is added.
//
// Both modes write the SAME two fields the controller reads — card_charge[amount_dollars]
// and card_charge[lease_id] — so the server path is identical either way. The
// amount stays editable in balance mode (tap $92.70, then trim it to a partial).
export default class extends Controller {
  static targets = [
    "amount", "leaseId", "runAmount",
    "balancePane", "otherPane", "tabBalance", "tabOther",
    "balanceRadio", "unitSelect"
  ]

  connect() {
    // Fresh load with balance rows: seed the amount + unit from the checked row,
    // unless the field already carries a value (an error re-render preserving what
    // the operator typed — don't stomp it).
    if (this.hasBalancePaneTarget) {
      const checked = this.#checkedBalance()
      if (checked && this.#num(this.amountTarget) === 0) this.#applyBalance(checked)
      this.showBalance()
    } else {
      this.showOther()
    }
    this.#updateRun()
  }

  showBalance() {
    if (!this.hasBalancePaneTarget) return
    this.#toggle(this.balancePaneTarget, true)
    this.#toggle(this.otherPaneTarget, false)
    this.#lightTab(this.tabBalanceTarget, true)
    this.#lightTab(this.tabOtherTarget, false)
    const checked = this.#checkedBalance()
    if (checked) this.#applyBalance(checked)
  }

  showOther() {
    this.#toggle(this.otherPaneTarget, true)
    if (this.hasBalancePaneTarget) {
      this.#toggle(this.balancePaneTarget, false)
      this.#lightTab(this.tabBalanceTarget, false)
      this.#lightTab(this.tabOtherTarget, true)
    }
    // In "other" mode the visible unit picker drives attribution.
    if (this.hasUnitSelectTarget) this.leaseIdTarget.value = this.unitSelectTarget.value
  }

  // A balance row was tapped.
  pickBalance(event) {
    this.#applyBalance(event.currentTarget)
    this.amountTarget.focus()
  }

  // The "other charge" unit picker changed.
  pickUnit() {
    this.leaseIdTarget.value = this.unitSelectTarget.value
  }

  // The operator edited the amount by hand (a partial off a tapped balance, or a
  // typed "other" amount).
  amountChanged() {
    this.#updateRun()
  }

  #applyBalance(radio) {
    radio.checked = true
    this.amountTarget.value = radio.dataset.amount
    this.leaseIdTarget.value = radio.dataset.leaseId || ""
    this.#updateRun()
  }

  #checkedBalance() {
    if (!this.hasBalanceRadioTarget) return null
    return this.balanceRadioTargets.find((r) => r.checked) || this.balanceRadioTargets[0]
  }

  #updateRun() {
    if (this.hasRunAmountTarget) {
      const cents = Math.round(this.#num(this.amountTarget) * 100)
      this.runAmountTarget.textContent = cents > 0 ? ` · ${this.#money(cents / 100)}` : ""
    }
  }

  #toggle(el, show) {
    el.classList.toggle("hidden", !show)
  }

  #lightTab(tab, on) {
    tab.classList.toggle("bg-white", on)
    tab.classList.toggle("text-primary-700", on)
    tab.classList.toggle("shadow-sm", on)
    tab.classList.toggle("text-secondary-600", !on)
    tab.setAttribute("aria-pressed", on ? "true" : "false")
  }

  #num(el) {
    const v = parseFloat(el.value)
    return Number.isNaN(v) ? 0 : v
  }

  #money(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" })
  }
}
